import "./GanttRoadmapRow.css";

import { JSX } from "react/jsx-runtime";

import { MARKER_CLUSTER_THRESHOLD, NEUTRAL_COLOR } from "../../constants";
import {
  GanttClusterItem,
  GanttKeyDate,
  GanttPhaseKey,
  GanttRoadmapMilestone,
  GanttRoadmapRowProps,
} from "../../models";
import {
  clusterByX,
  dateToPixel,
  findPhaseKeyAtDate,
  findStageIndexAtDate,
  getLaneStackHeight,
  getLaneTop,
  getPhaseColor,
  getRoadmapRowHeight,
  getStageColor,
  packPhaseLanes,
} from "../../utils";
import { GanttBar } from "../GanttBar";
import { GanttMarkerCluster } from "../GanttMarkerCluster";
import { GanttTodayLine } from "../GanttTodayLine";

/** Representative colour for a cluster: the shared item colour, or neutral when mixed. */
const clusterColor = (colors: string[]): string => (colors.every((c) => c === colors[0]) ? colors[0] : NEUTRAL_COLOR);

export const GanttRoadmapRow = ({
  row,
  mode,
  viewStart,
  pixelsPerDay,
  timelineWidth,
  todayX,
  labelWidth,
  onClick,
  onPhaseClick,
  onMilestoneClick,
  onKeyDateClick,
  onShowTooltip,
  onHideTooltip,
}: GanttRoadmapRowProps): JSX.Element => {
  const ppd = pixelsPerDay;

  const { bars, laneCount } = packPhaseLanes(row.phases);
  const rowHeight = getRoadmapRowHeight(laneCount);
  const keyDateHeight = getLaneStackHeight(laneCount);
  const stageCount = row.phases.length;

  const colorAtDate = (date: Date, explicitPhase?: GanttPhaseKey): string => {
    if (mode === "stage") {
      const stageIndex = findStageIndexAtDate(date, row.phases);
      return stageIndex >= 0 ? getStageColor(stageIndex, stageCount).bar : NEUTRAL_COLOR;
    }
    const phaseKey = findPhaseKeyAtDate(date, row.phases) ?? explicitPhase;
    return phaseKey ? getPhaseColor(phaseKey).bar : NEUTRAL_COLOR;
  };

  const firePhase = (id: string): void => (onPhaseClick ? onPhaseClick(id, row.id) : onClick?.(row.id));
  const fireMilestone = (id: string): void => (onMilestoneClick ? onMilestoneClick(id, row.id) : onClick?.(row.id));
  const fireKeyDate = (id: string): void => (onKeyDateClick ? onKeyDateClick(id, row.id) : onClick?.(row.id));

  // Cluster markers that would otherwise overlap at the current zoom.
  const milestoneClusters = clusterByX(
    row.milestones.map((mi) => ({ item: mi, x: dateToPixel(mi.date, viewStart, ppd) })),
    MARKER_CLUSTER_THRESHOLD
  );
  const keyDateClusters = clusterByX(
    (row.keyDates ?? []).map((kd) => ({ item: kd, x: dateToPixel(kd.date, viewStart, ppd) })),
    MARKER_CLUSTER_THRESHOLD
  );

  const milestoneItem = (mi: GanttRoadmapMilestone): GanttClusterItem => ({
    id: mi.id,
    label: mi.label,
    detail: mi.date.toLocaleDateString(),
    color: colorAtDate(mi.date, mi.phase),
    onSelect: () => fireMilestone(mi.id),
  });

  const keyDateItem = (kd: GanttKeyDate): GanttClusterItem => ({
    id: kd.id,
    label: kd.label,
    detail: kd.type ? `${kd.type} · ${kd.date.toLocaleDateString()}` : kd.date.toLocaleDateString(),
    color: colorAtDate(kd.date, kd.phase),
    onSelect: () => fireKeyDate(kd.id),
  });

  // In stage mode a deal can be linked to zero, one, or several projects; when
  // `relatedProjects` is provided it replaces the static subtitle.
  const renderMeta = (): JSX.Element | false => {
    if (row.relatedProjects !== undefined) {
      if (row.relatedProjects.length === 0) {
        return <span className="GanttRoadmapSubtitle GanttRoadmapRelatedEmpty body4">No related project</span>;
      }
      const joined = row.relatedProjects.join(", ");
      return (
        <span className="GanttRoadmapSubtitle GanttRoadmapRelated body4" title={joined}>
          {joined}
        </span>
      );
    }
    return !!row.subtitle && <span className="GanttRoadmapSubtitle body4">{row.subtitle}</span>;
  };

  const labelChildren = (
    <>
      <span className="GanttRoadmapName body2" title={row.name}>
        {row.name}
      </span>
      {renderMeta()}
    </>
  );

  return (
    <div className="GanttRoadmapRow" style={{ height: rowHeight }}>
      {/* ── Label ──────────────────────────────────────────────────────── */}
      {onClick ? (
        <button
          type="button"
          className="GanttRoadmapRowLabel"
          style={{ width: labelWidth }}
          onClick={() => onClick(row.id)}
        >
          {labelChildren}
        </button>
      ) : (
        <div className="GanttRoadmapRowLabel" style={{ width: labelWidth }}>
          {row.href ? (
            <a className="GanttRoadmapName body2" href={row.href} title={row.name}>
              {row.name}
            </a>
          ) : (
            <span className="GanttRoadmapName body2" title={row.name}>
              {row.name}
            </span>
          )}
          {renderMeta()}
        </div>
      )}

      {/* ── Timeline ───────────────────────────────────────────────────── */}
      <div className="GanttRoadmapRowTimeline" style={{ width: timelineWidth }}>
        <GanttTodayLine x={todayX} />

        {/* Phase/stage bars (labels are intentionally omitted — see the legend) */}
        {bars.map((phase) => {
          const x = dateToPixel(phase.startDate, viewStart, ppd);
          const xEnd = dateToPixel(phase.endDate, viewStart, ppd);
          const width = Math.max(xEnd - x, 4);
          // Colour comes from the bar's position in start-date order, never from its lane.
          const { bar, bg, border } =
            mode === "stage" ? getStageColor(phase.colorIndex, stageCount) : getPhaseColor(phase.phase);
          const tooltipLabel = `${phase.startDate.toLocaleDateString()} – ${phase.endDate.toLocaleDateString()}`;
          return (
            <GanttBar
              key={phase.id}
              variant="phase"
              x={x}
              width={width}
              top={getLaneTop(phase.lane)}
              rowHeight={rowHeight}
              color={bar}
              bgColor={bg}
              borderColor={border}
              title={`${phase.label}: ${tooltipLabel}`}
              onClick={() => firePhase(phase.id)}
              onMouseEnter={(e) =>
                onShowTooltip?.(
                  {
                    title: phase.label,
                    accentColor: bar,
                    meta: [
                      { label: "Project", value: row.name },
                      ...(phase.status ? [{ label: "Status", value: phase.status }] : []),
                      { label: "Dates", value: tooltipLabel },
                    ],
                  },
                  e
                )
              }
              onMouseLeave={onHideTooltip}
            />
          );
        })}

        {/* Key dates — thin full-row bars, drawn under the milestone diamonds */}
        {keyDateClusters.map((cluster) => {
          if (cluster.items.length === 1) {
            const kd = cluster.items[0];
            const color = colorAtDate(kd.date, kd.phase);
            const dateLabel = kd.date.toLocaleDateString();
            return (
              <GanttBar
                key={kd.id}
                variant="key-date"
                x={cluster.x}
                rowHeight={rowHeight}
                height={keyDateHeight}
                color={color}
                title={`${kd.label}: ${dateLabel}`}
                onClick={() => fireKeyDate(kd.id)}
                onMouseEnter={(e) =>
                  onShowTooltip?.(
                    {
                      title: kd.label,
                      accentColor: color,
                      meta: [
                        { label: "Project", value: row.name },
                        ...(kd.type ? [{ label: "Type", value: kd.type }] : []),
                        { label: "Date", value: dateLabel },
                      ],
                    },
                    e
                  )
                }
                onMouseLeave={onHideTooltip}
              />
            );
          }
          const items = cluster.items.map(keyDateItem);
          return (
            <GanttMarkerCluster
              key={cluster.items.map((kd) => kd.id).join("_")}
              x={cluster.x}
              rowHeight={rowHeight}
              markerHeight={keyDateHeight}
              shape="line"
              color={clusterColor(items.map((it) => it.color))}
              items={items}
              noun="key dates"
              onShowTooltip={onShowTooltip}
              onHideTooltip={onHideTooltip}
            />
          );
        })}

        {/* Milestone diamonds — coloured to match the phase/stage they fall within */}
        {milestoneClusters.map((cluster) => {
          if (cluster.items.length === 1) {
            const mi = cluster.items[0];
            const color = colorAtDate(mi.date, mi.phase);
            return (
              <GanttBar
                key={mi.id}
                variant="roadmap-milestone"
                x={cluster.x}
                rowHeight={rowHeight}
                color={color}
                title={`${mi.label}: ${mi.date.toLocaleDateString()}`}
                onClick={() => fireMilestone(mi.id)}
                onMouseEnter={(e) =>
                  onShowTooltip?.(
                    {
                      title: mi.label,
                      accentColor: color,
                      meta: [
                        { label: "Project", value: row.name },
                        { label: "Milestone", value: mi.date.toLocaleDateString() },
                      ],
                    },
                    e
                  )
                }
                onMouseLeave={onHideTooltip}
              />
            );
          }
          const items = cluster.items.map(milestoneItem);
          return (
            <GanttMarkerCluster
              key={cluster.items.map((mi) => mi.id).join("_")}
              x={cluster.x}
              rowHeight={rowHeight}
              shape="diamond"
              color={clusterColor(items.map((it) => it.color))}
              items={items}
              noun="milestones"
              onShowTooltip={onShowTooltip}
              onHideTooltip={onHideTooltip}
            />
          );
        })}
      </div>
    </div>
  );
};
